import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import {
  buildButtonColorVarDeclarations,
  buildButtonStyleRules,
  buildTertiaryColorRules,
} from "./build-button-css-rules";
import { buildCheckboxCssRules } from "./build-checkbox-css-rules";
import { buildInputFieldRules } from "./build-input-field-css-rules";
import { buildItemCardCssRules } from "./build-item-card-css-rules";
import {
  buildTypographySelectorRules,
  buildTypographyVarDeclarations,
} from "./build-typography-css-rules";

export function buildPortalCustomizationCss(
  customization: PortalCustomization,
  { scope = "" }: { scope?: string } = {},
): string {
  // `s`: descendant-selector prefix (empty in global mode). `root`: selector
  // for setting vars ON the scope element itself (`:root` globally).
  const s = scope ? `${scope} ` : "";
  const root = scope || ":root";

  const typography = {
    accentColor: customization.colors?.primary ?? undefined,
    headingFont: customization.typography?.fonts?.heading ?? undefined,
    bodyFont: customization.typography?.fonts?.body ?? undefined,
    primaryText: customization.typography?.textColors?.primary ?? undefined,
    secondaryText: customization.typography?.textColors?.secondary ?? undefined,
  };
  const primaryButton = customization.buttons?.primary;
  const secondaryButton = customization.buttons?.secondary;
  const tertiaryButton = customization.buttons?.tertiary;
  const backToStoreText =
    customization.pages?.login?.colors?.backToStoreText ?? undefined;

  // 1. CSS variables set on the scope root.
  const cssVarDeclarations: string[] = [
    ...buildTypographyVarDeclarations(typography),
    ...buildButtonColorVarDeclarations("primary", primaryButton?.colors),
    ...buildButtonColorVarDeclarations("secondary", secondaryButton?.colors),
  ];
  const rootVarsRule =
    cssVarDeclarations.length > 0
      ? `${root} { ${cssVarDeclarations.join(" ")} }`
      : undefined;

  // 2. Typography selector rules (font-family, color, link color).
  const typographySelectorRules = buildTypographySelectorRules(
    s,
    root,
    typography,
  );

  // 3. Button style rules (radius, border, padding, font) per hierarchy.
  const primaryButtonStyleRules = buildButtonStyleRules(
    s,
    "primary",
    primaryButton,
  );
  const secondaryButtonStyleRules = buildButtonStyleRules(
    s,
    "secondary",
    secondaryButton,
  );
  const tertiaryButtonStyleRules = buildButtonStyleRules(
    s,
    "tertiary",
    tertiaryButton,
  );

  // 4. Tertiary button colors. Tertiary reuses secondary's fg token in the
  // design system, so apply its colors as direct selector rules instead of
  // CSS vars.
  const tertiaryColorRules = buildTertiaryColorRules(s, tertiaryButton?.colors);

  // 5. Input field rules.
  const inputFieldRules = buildInputFieldRules(s, customization.inputFields);

  // 6. Checkbox color rules (selection cards).
  const checkboxRules = buildCheckboxCssRules(s, customization.style?.colors);

  // 7. Item card style rules (reuses selection card border/radius/padding).
  const itemCardRules = buildItemCardCssRules(s, customization.style);

  // 8. Login page — back-to-store link. `!important` beats the inline
  // `style={{ color }}` on the login.tsx link.
  const backToStoreRule = backToStoreText
    ? `${s}[data-ui="back-to-store"] { color: ${backToStoreText} !important; }`
    : undefined;

  const rules = [
    rootVarsRule,
    ...typographySelectorRules,
    ...primaryButtonStyleRules,
    ...secondaryButtonStyleRules,
    ...tertiaryButtonStyleRules,
    ...tertiaryColorRules,
    ...inputFieldRules,
    ...checkboxRules,
    ...itemCardRules,
    backToStoreRule,
  ].filter((rule): rule is string => rule !== undefined);

  return rules.join("\n");
}
